import { useEffect, useState } from "react";
import type { MainStrategyEnum } from "../../apiClient/generated";
import { ApiValidationError } from "./api";
import type { EditorMetadata, WorkflowDefinition } from "./types";
import type { EditorMutations } from "./useEditorMutations";

/**
 * The definition's own fields, and its event-linking rule.
 *
 * Owner is not editable: moving a definition between companies would invalidate every reference
 * param configured on it without saying so. Duplicating to another company is the supported route
 * and clears them deliberately — that is still done from the admin change form.
 */
export function DefinitionForm({
    definition,
    metadata,
    mutations,
    onDirtyChange,
}: {
    definition: WorkflowDefinition;
    metadata: EditorMetadata;
    mutations: EditorMutations;
    onDirtyChange: (key: string, dirty: boolean) => void;
}) {
    const [name, setName] = useState(definition.name);
    const [description, setDescription] = useState(definition.description);
    const [isActive, setIsActive] = useState(definition.isActive);
    const [autoLinking, setAutoLinking] = useState(definition.autoEventLinking);

    useEffect(() => {
        setName(definition.name);
        setDescription(definition.description);
        setIsActive(definition.isActive);
        setAutoLinking(definition.autoEventLinking);
    }, [
        definition.name,
        definition.description,
        definition.isActive,
        definition.autoEventLinking,
    ]);

    const isDirty =
        name !== definition.name ||
        description !== definition.description ||
        isActive !== definition.isActive ||
        autoLinking !== definition.autoEventLinking;

    useEffect(() => {
        onDirtyChange("definition", isDirty);
        return () => onDirtyChange("definition", false);
    }, [isDirty, onDirtyChange]);

    const save = mutations.saveDefinition;
    const fieldErrors =
        save.error instanceof ApiValidationError ? save.error.fieldErrors : {};

    return (
        <section className="aer-wf-section">
            <h3>Definition</h3>

            <div className="aer-wf-fields">
                <div className="aer-wf-field">
                    <label htmlFor="aer-wf-name">Name</label>
                    <input
                        id="aer-wf-name"
                        type="text"
                        maxLength={50}
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                    />
                    {fieldErrors.name && (
                        <p className="aer-wf-field__error" role="alert">
                            {fieldErrors.name.join(" ")}
                        </p>
                    )}
                </div>

                <div className="aer-wf-field">
                    <label htmlFor="aer-wf-description">Description</label>
                    <textarea
                        id="aer-wf-description"
                        rows={2}
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                    />
                </div>

                <div className="aer-wf-field aer-wf-field--inline">
                    <input
                        id="aer-wf-active"
                        type="checkbox"
                        checked={isActive}
                        onChange={(event) => setIsActive(event.target.checked)}
                    />
                    <label htmlFor="aer-wf-active">Active</label>
                    <p className="aer-wf-field__hint">
                        Inactive definitions are not used for new workflows.
                    </p>
                </div>

                <div className="aer-wf-field aer-wf-field--inline">
                    <input
                        id="aer-wf-linking"
                        type="checkbox"
                        checked={autoLinking}
                        onChange={(event) =>
                            setAutoLinking(event.target.checked)
                        }
                    />
                    <label htmlFor="aer-wf-linking">
                        Automatic event linking
                    </label>
                </div>
            </div>

            <div className="aer-wf-rule__save">
                <button
                    type="button"
                    className="aer-wf-button"
                    disabled={!isDirty || save.isLoading}
                    onClick={() =>
                        save.mutate({
                            name,
                            description,
                            isActive: isActive,
                            autoEventLinking: autoLinking,
                        })
                    }
                >
                    {save.isLoading ? "Saving…" : "Save definition"}
                </button>
                {isDirty && <span className="aer-wf-rule__dirty">Unsaved</span>}
            </div>

            {/* Keyed off the draft, not the saved value: someone who has just ticked the box
                expects the rule to configure, and a rule can exist perfectly well before
                linking is switched on. */}
            {autoLinking && (
                <LinkingRuleForm
                    definition={definition}
                    metadata={metadata}
                    mutations={mutations}
                    onDirtyChange={onDirtyChange}
                    linkingUnsaved={autoLinking !== definition.autoEventLinking}
                />
            )}
        </section>
    );
}

function LinkingRuleForm({
    definition,
    metadata,
    mutations,
    onDirtyChange,
    linkingUnsaved,
}: {
    definition: WorkflowDefinition;
    metadata: EditorMetadata;
    mutations: EditorMutations;
    onDirtyChange: (key: string, dirty: boolean) => void;
    linkingUnsaved: boolean;
}) {
    const rule = definition.linkingRule;

    // The guard lives above any hook, so the two branches below can use hooks unconditionally.
    return (
        <div className="aer-wf-subsection">
            <h4>Event linking</h4>

            {linkingUnsaved && (
                <p className="aer-wf-field__hint aer-wf-field__hint--warn">
                    Save the definition to switch automatic linking on. The rule
                    below can be configured either way.
                </p>
            )}

            {rule ? (
                <LinkingRuleFields
                    rule={rule}
                    metadata={metadata}
                    mutations={mutations}
                    onDirtyChange={onDirtyChange}
                />
            ) : (
                <NewLinkingRuleForm metadata={metadata} mutations={mutations} />
            )}
        </div>
    );
}

/**
 * Create the linking rule for a definition that has none.
 *
 * Worth having rather than linking out to the admin: automatic linking with no rule is a state
 * the engine handles by logging a warning and skipping linking entirely, so it looks configured
 * and does nothing.
 */
function NewLinkingRuleForm({
    metadata,
    mutations,
}: {
    metadata: EditorMetadata;
    mutations: EditorMutations;
}) {
    const [provider, setProvider] = useState("");
    const [strategy, setStrategy] = useState<MainStrategyEnum>(
        (metadata.linkingStrategies[0]?.value as MainStrategyEnum) ?? "oldest",
    );
    const [fieldsText, setFieldsText] = useState("");

    const add = mutations.addLinkingRule;
    const fieldErrors =
        add.error instanceof ApiValidationError ? add.error.fieldErrors : {};

    const parsedFields = fieldsText
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

    return (
        <>
            <div className="aer-wf-notice aer-wf-notice--warn">
                <p>
                    No linking rule is configured. Until there is one, the
                    engine logs a warning and skips linking — nothing is
                    grouped.
                </p>
            </div>

            <div className="aer-wf-fields">
                <div className="aer-wf-field">
                    <label htmlFor="aer-wf-new-strategy">Main event</label>
                    <select
                        id="aer-wf-new-strategy"
                        value={strategy}
                        onChange={(event) =>
                            setStrategy(event.target.value as MainStrategyEnum)
                        }
                    >
                        {metadata.linkingStrategies.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="aer-wf-field">
                    <label htmlFor="aer-wf-new-provider">Data provider</label>
                    <select
                        id="aer-wf-new-provider"
                        value={provider}
                        onChange={(event) => setProvider(event.target.value)}
                    >
                        <option value="">Any provider</option>
                        {metadata.dataProviders.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="aer-wf-field">
                    <label htmlFor="aer-wf-new-grouping">Grouping fields</label>
                    <textarea
                        id="aer-wf-new-grouping"
                        rows={3}
                        value={fieldsText}
                        onChange={(event) => setFieldsText(event.target.value)}
                    />
                    <p className="aer-wf-field__hint">
                        One field path per line, e.g. <code>site_id</code> or{" "}
                        <code>data_point.data.custom_field</code>.
                    </p>
                    {fieldErrors.grouping_fields && (
                        <p className="aer-wf-field__error" role="alert">
                            {fieldErrors.grouping_fields.join(" ")}
                        </p>
                    )}
                </div>
            </div>

            {fieldErrors.workflow && (
                <p className="aer-wf-field__error" role="alert">
                    {fieldErrors.workflow.join(" ")}
                </p>
            )}

            <div className="aer-wf-rule__save">
                <button
                    type="button"
                    className="aer-wf-button"
                    disabled={!parsedFields.length || add.isLoading}
                    onClick={() =>
                        add.mutate({
                            dataProvider: provider || null,
                            mainStrategy: strategy,
                            groupingFields: parsedFields,
                        })
                    }
                >
                    {add.isLoading ? "Creating…" : "Create linking rule"}
                </button>
                {!parsedFields.length && (
                    <span className="aer-wf-field__hint">
                        At least one grouping field is needed — it is what
                        decides which events belong together.
                    </span>
                )}
            </div>
        </>
    );
}

function LinkingRuleFields({
    rule,
    metadata,
    mutations,
    onDirtyChange,
}: {
    rule: NonNullable<WorkflowDefinition["linkingRule"]>;
    metadata: EditorMetadata;
    mutations: EditorMutations;
    onDirtyChange: (key: string, dirty: boolean) => void;
}) {
    const [provider, setProvider] = useState(rule.dataProvider ?? "");
    const [strategy, setStrategy] = useState(rule.mainStrategy);
    // Edited as one path per line: the stored value is a JSON array of field paths, and a raw
    // JSON textarea is a worse editing experience than a list.
    const [fieldsText, setFieldsText] = useState(
        (rule.groupingFields ?? []).join("\n"),
    );

    useEffect(() => {
        setProvider(rule.dataProvider ?? "");
        setStrategy(rule.mainStrategy);
        setFieldsText((rule.groupingFields ?? []).join("\n"));
    }, [rule.dataProvider, rule.mainStrategy, rule.groupingFields]);

    const parsedFields = fieldsText
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

    const isDirty =
        provider !== (rule.dataProvider ?? "") ||
        strategy !== rule.mainStrategy ||
        parsedFields.join("\n") !== (rule.groupingFields ?? []).join("\n");

    useEffect(() => {
        onDirtyChange("linking-rule", isDirty);
        return () => onDirtyChange("linking-rule", false);
    }, [isDirty, onDirtyChange]);

    const save = mutations.saveLinkingRule;
    const fieldErrors =
        save.error instanceof ApiValidationError ? save.error.fieldErrors : {};

    return (
        <>
            {/* Grouping fields that match no data are informative, not fatal. */}
            {rule.warnings.length > 0 && (
                <div className="aer-wf-notice aer-wf-notice--warn">
                    <ul>
                        {rule.warnings.map((warning) => (
                            <li key={warning}>{warning}</li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="aer-wf-fields">
                <div className="aer-wf-field">
                    <label htmlFor="aer-wf-provider">Data provider</label>
                    <select
                        id="aer-wf-provider"
                        value={provider}
                        onChange={(event) => setProvider(event.target.value)}
                    >
                        <option value="">Any provider</option>
                        {metadata.dataProviders.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="aer-wf-field">
                    <label htmlFor="aer-wf-strategy">Main event</label>
                    <select
                        id="aer-wf-strategy"
                        value={strategy}
                        onChange={(event) =>
                            setStrategy(event.target.value as MainStrategyEnum)
                        }
                    >
                        {metadata.linkingStrategies.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="aer-wf-field">
                    <label htmlFor="aer-wf-grouping">Grouping fields</label>
                    <textarea
                        id="aer-wf-grouping"
                        rows={3}
                        value={fieldsText}
                        onChange={(event) => setFieldsText(event.target.value)}
                    />
                    <p className="aer-wf-field__hint">
                        One field path per line, e.g. <code>site_id</code> or{" "}
                        <code>data_point.data.custom_field</code>.
                    </p>
                    {fieldErrors.grouping_fields && (
                        <p className="aer-wf-field__error" role="alert">
                            {fieldErrors.grouping_fields.join(" ")}
                        </p>
                    )}
                </div>
            </div>

            <div className="aer-wf-rule__save">
                <button
                    type="button"
                    className="aer-wf-button"
                    disabled={!isDirty || save.isLoading}
                    onClick={() =>
                        save.mutate({
                            id: rule.id,
                            fields: {
                                dataProvider: provider || null,
                                mainStrategy: strategy,
                                groupingFields: parsedFields,
                            },
                        })
                    }
                >
                    {save.isLoading ? "Saving…" : "Save linking rule"}
                </button>
                {isDirty && <span className="aer-wf-rule__dirty">Unsaved</span>}
                <button
                    type="button"
                    className="aer-wf-button aer-wf-button--secondary"
                    disabled={mutations.removeLinkingRule.isLoading}
                    onClick={() => mutations.removeLinkingRule.mutate(rule.id)}
                >
                    Remove linking rule
                </button>
            </div>
            <p className="aer-wf-field__hint">
                Turning automatic linking off leaves this rule in place — it
                simply stops being used. Remove it to delete it.
            </p>
        </>
    );
}
